import { Grid, Theme, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import NavigationBar from '../../../components/NavigationBar';
import { PageTitle } from '../../../components/PageTitle/PageTitle';
import { useUniversalTracking } from '../../../lib/tracking/universalTracking';
import {
	finalizeProcessByEmail,
	finalizeProcessByPrint,
} from '../../../redux/actions/contractSignature';
import { colors } from '../../../theme/theme';
import {AppState, ContractState, FahrzeugType, SalesType} from '../../../types/redux';
import {
	formatDateEn2De,
	formatIban,
	formatPriceDe, formatVehicel,
} from '../../../utils/formatString';
import { SelectSignatureModal } from './components/SelectSignatureModal/SelectSignatureModal';
import {
	SELECTED_NOW,
	SELECTED_LATER
} from "../../../redux/reducers/collectedStateReducer";

const useStyles = makeStyles((theme: Theme) => ({
	main: {
		backgroundColor: colors.white,
	},
	section: {
		padding: theme.spacing(3, 4, 0),
	},
	sectionTitle: {
		marginBottom: theme.spacing(3),
	},
	sectionItem: {
		marginBottom: theme.spacing(3),
	},
	sectionItemTitle: {
		fontWeight: 400,
	},
}));

interface SectionItemProps {
	title: string;
	value?: string;
	values?: string[];
}

const SectionItem: React.FC<SectionItemProps> = (props) => {
	const classes = useStyles();

	return (
		<div className={`${classes.sectionItem}`}>
			<Typography
				variant="subtitle2"
				color="textSecondary"
				className={`${classes.sectionItemTitle}`}
			>
				{props.title}
			</Typography>
			<Typography variant="body1">
				{!!props.value && props.value}
				{!!props.values &&
					props.values.map((val, i) => (
						<Fragment key={i}>
							{val}
							<br />
						</Fragment>
					))}
			</Typography>
		</div>
	);
};

interface SectionProps {
	title: string;
}

const Section: React.FC<SectionProps> = (props) => {
	const classes = useStyles();

	return (
		<Grid item xs={12} sm={6} md={3} className={classes.section}>
			<Typography variant="h3" className={`${classes.sectionTitle}`}>
				{props.title}
			</Typography>
			{props.children}
		</Grid>
	);
};

interface KppReviewProps extends StateProps, DispatchProps {
	onSubmit(data: any, goToEnd?: boolean): void;
}

const InternalKppReview: React.FC<KppReviewProps> = (props) => {
	const { t } = useTranslation();
	const { trackEvent } = useUniversalTracking();
	const classes = useStyles();
	const { contract } = props;
	const [showModal, setShowModal] = useState(false);
	const [sendEmailErrorMessage, setSendEmailErrorMessage] =
		useState<string | null>(null);

	// continue -> show modal
	const onClickContinue = () => {
		setShowModal(true);
	};

	// advance to edoc signature
	const onSelectSignDigital = () => {
		trackEvent({
			category: 'review-submit',
			action: 'click-event',
			label: 'Select edocbox signature',
		});
		props.onSubmit({});
	};

	// print contract, finalize process
	const onSelectSignPrint = () => {
		trackEvent({
			category: 'review-submit',
			action: 'click-event',
			label: 'Select print contract',
		});
		props.finalizeByPrint();
	};

	// send email, finalize contract
	const onSelectSignEmail = async () => {
		trackEvent({
			category: 'review-submit',
			action: 'click-event',
			label: 'Select send contract per email',
		});

		return props
			.finalizeByEmail()
			.then(() => {
				props.onSubmit({}, true);
			})
			.catch((errorMsg) => {
				setSendEmailErrorMessage(errorMsg);
			});
	};

	return (
		<div className="kpp_review_main">
			<PageTitle title={t('kppReview.title')} />
			<Grid container justifyContent="flex-start" className={classes.main}>
				<Section title={t('kppReview.vehicle')}>
					<SectionItem
						title={t('kppReview.vehicletype')}
						value={formatVehicel(contract.fahrzeugtyp as FahrzeugType)}
					/>
					<SectionItem
						title={t('kppReview.buyDate')}
						value={formatDateEn2De(contract.kaufdatum)}
					/>
					<SectionItem
						title={t('kppReview.carState')}
						value={
							contract.neufahrzeug
								? t('kppReview.carStateNew')
								: t('kppReview.carStateUsed')
						}
					/>
				</Section>
				<Section title={t('kppReview.insurance')}>
					<SectionItem
						title={t('kppReview.insuranceProduct')}
						value={`${t(`home.${contract.salesType}.title`)} ${contract.laufzeit}`}
					/>
					<SectionItem
						title={t('kppReview.amountToBePaid')}
						value={`${formatPriceDe(contract.beitrag)}€/${
							contract.salesType === SalesType.KPP ? 'Monat' : 'einmalig'
						}`}
					/>
					<SectionItem
						title={t('kppReview.beginDate')}
						value={formatDateEn2De(contract.beginndatum)}
					/>
					{!contract.neufahrzeug && (
						<SectionItem
							title={t('kppReview.firstRegistrationAfter')}
							value={formatDateEn2De(contract.earliestRegistrationDate)}
						/>
					)}
				</Section>
				<Section title={t('kppReview.client')}>
					<SectionItem
						title={t('kppReview.address')}
						values={[
							`${contract.vorname} ${contract.name}`,
							`${contract.street} ${contract.streetNumber}`,
							`${contract.plz} ${contract.ort}`,
						]}
					/>
					<SectionItem
						title={t('kppReview.birthDate')}
						value={formatDateEn2De(contract.geburtsdatum)}
					/>
					<SectionItem title={t('kppReview.email')} value={contract.email} />
					{!!contract.telefonnummer && (
						<SectionItem
							title={t('kppReview.telephone')}
							value={contract.telefonnummer}
						/>
					)}
				</Section>
				<Section title={t('kppReview.account')}>
					<SectionItem
						title={t('kppReview.iban')}
						value={formatIban(contract.iban)}
					/>
					<SectionItem title={t('kppReview.bic')} value={contract.bic} />
					<SectionItem title={t('kppReview.bank')} value={contract.bank} />
				</Section>
			</Grid>
			<NavigationBar onClickContinue={onClickContinue} />
			<SelectSignatureModal
				showModal={showModal}
				onCancel={() => setShowModal(false)}
				onSelectSignDigital={onSelectSignDigital}
				onSelectSendEmail={onSelectSignEmail}
				onSelectSignPrint={onSelectSignPrint}
				sendEmailErrorMessage={sendEmailErrorMessage}
			/>
		</div>
	);
};

const mapStateToProps = (state: AppState) => ({
	contract: state.contract,
});

/* istanbul ignore next */
const mapDispatchToProps = (
	dispatch: ThunkDispatch<Promise<any>, void, Action>
) => ({
	finalizeByEmail: async () => {
		dispatch({type: SELECTED_LATER});
		dispatch(finalizeProcessByEmail())
	},
	finalizeByPrint: () => {
		dispatch({ type: SELECTED_NOW });
		dispatch(finalizeProcessByPrint());
	},
});

interface StateProps {
	contract: ContractState;
}

interface DispatchProps {
	finalizeByEmail(): Promise<void>;
	finalizeByPrint(): void;
}

export const KppReview = connect(
	mapStateToProps,
	mapDispatchToProps
)(InternalKppReview);
